/**
 * Input system — three reusable form controls that all share the same
 * label / tooltip / error / hint wrapper pattern.
 *
 * Exports:
 *   default  Input     — text, email, url, password, number, search, etc.
 *   named    Select    — <select> dropdown with auto chevron
 *   named    Textarea  — resizable multiline with optional char/word counter
 *
 * Shared props (all three):
 *   label       string    — field label (uppercase, small caps style)
 *   required    bool      — appends red asterisk to label
 *   labelHint   string    — optional right-aligned hint next to the label
 *   labelIcon   ReactElement  — optional icon shown to the LEFT of the label
 *                               text (inside the <label>, before it).
 *                               e.g. <IconMessage2 size={14} />
 *   tooltip     string    — optional tooltip text; shows an amber info button
 *                           that toggles open on click and closes on outside click
 *   error       string    — error message; also triggers red border
 *   hint        string    — helper text shown below the input (when no error)
 *   size        'sm' | 'md' | 'lg'   (default 'md')
 *   id          string    — wires <label htmlFor> AND the input/select/textarea's id
 *   htmlFor     string    — optional override for the label's `for` attribute,
 *                           in case it needs to differ from `id`. Defaults to `id`.
 *   className   string    — merged onto the root .field wrapper
 *   shake       bool      — triggers shake animation (for failed validation)
 *
 * Input-only props:
 *   leftIcon    ReactElement  — icon inside left edge  e.g. <IconSearch size={16} />
 *   rightIcon   ReactElement  — icon inside right edge
 *   ...rest     — forwarded to <input> (type, value, onChange, placeholder, etc.)
 *
 * Select-only props:
 *   children    — <option> elements
 *   chevron     ReactElement  — override the default IconChevronDown
 *   ...rest     — forwarded to <select>
 *
 * Textarea-only props:
 *   maxLength   number     — enables character counter when provided
 *   maxWords    number     — enables word counter instead (display only, doesn't block typing)
 *   rows        number     — maps to <textarea rows>
 *   ...rest     — forwarded to <textarea>
 *
 * ─── Usage examples ───────────────────────────────────────────────────────
 *
 * // Basic text input
 * <Input label="Company Name" required id="name"
 *   value={name} onChange={e => setName(e.target.value)} />
 *
 * // With left icon + error
 * <Input label="Company Website" id="website" type="url"
 *   leftIcon={<IconWorld size={16} />}
 *   error={errors.website}
 *   value={url} onChange={e => setUrl(e.target.value)} />
 *
 * // With tooltip
 * <Input label="CAC Number" id="cac" required
 *   tooltip="Your Corporate Affairs Commission registration number."
 *   value={cac} onChange={e => setCac(e.target.value)} />
 *
 * // With a label icon (to the left of the label text)
 * <Input label="Summary of Feedback" id="summary"
 *   labelIcon={<IconMessage2 size={14} />}
 *   value={summary} onChange={e => setSummary(e.target.value)} />
 *
 * // Select dropdown
 * <Select label="Position" required id="position"
 *   value={position} onChange={e => setPosition(e.target.value)}
 *   error={errors.position}>
 *   <option value="" disabled>Select position…</option>
 *   <option value="CTO">CTO</option>
 * </Select>
 *
 * // Textarea with char counter
 * <Textarea label="About Company" id="about" maxLength={500}
 *   value={about} onChange={e => setAbout(e.target.value)} />
 *
 * // Textarea with word counter
 * <Textarea label="Job Description" id="desc" maxWords={250}
 *   value={desc} onChange={e => setDesc(e.target.value)} />
 */

import { forwardRef, useState, useEffect, useRef } from "react";
import {
  IconAlertCircle,
  IconChevronDown,
  IconInfoCircle,
} from "@tabler/icons-react";
import styles from "./input.module.css";
import classes from "./input.module.css";

// ── Info tooltip button — toggles open on click, closes on outside click ────
export function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  return (
    <button
      ref={btnRef}
      type="button"
      className={styles.infoBtn}
      aria-label="More information"
      onClick={(e) => {
        e.stopPropagation();
        setOpen((o) => !o);
      }}
    >
      <IconInfoCircle size={15} />
      <span className={`${styles.tooltip} ${open ? styles.tooltipOpen : ""}`}>
        {text}
      </span>
    </button>
  );
}

// ── Shared field wrapper ── //
function FieldWrapper({
  label,
  required,
  labelHint,
  labelIcon,
  tooltip,
  error,
  hint,
  id,
  htmlFor,
  className,
  children,
  labelVariant = "default", // "default" | "uppercase"
}) {
  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      {label && (
        <div className={styles.labelRow}>
          <label
            className={[
              styles.label,
              labelVariant === "uppercase" && styles.labelUppercase,
            ]
              .filter(Boolean)
              .join(" ")}
            htmlFor={htmlFor ?? id}
          >
            {labelIcon && (
              <span className={styles.labelIcon} aria-hidden="true">
                {labelIcon}
              </span>
            )}
            {label}
            {required && <span className={styles.req}>*</span>}
            {tooltip && <InfoTooltip text={tooltip} />}
          </label>
          {labelHint && <span className={styles.labelHint}>{labelHint}</span>}
        </div>
      )}

      {children}

      {error && (
        <div className={styles.error} role="alert">
          <IconAlertCircle size={13} />
          {error}
        </div>
      )}

      {!error && hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
}

// ── Input ── //
const Input = forwardRef(function Input(
  {
    label,
    required,
    labelHint,
    labelIcon,
    tooltip,
    error,
    hint,
    id,
    htmlFor,
    size = "md",
    leftIcon,
    rightIcon,
    shake = false,
    className = "",
    labelVariant,
    ...rest
  },
  ref,
) {
  const inputClass = [
    styles.input,
    styles[size],
    leftIcon ? styles.hasIconLeft : "",
    rightIcon ? styles.hasIconRight : "",
    error ? styles.inputError : "",
    shake ? styles.shake : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <FieldWrapper
      label={label}
      required={required}
      labelHint={labelHint}
      labelIcon={labelIcon}
      tooltip={tooltip}
      error={error}
      hint={hint}
      id={id}
      htmlFor={htmlFor}
      className={className}
      labelVariant={labelVariant}
    >
      <div className={styles.inputWrap}>
        {leftIcon && (
          <span className={styles.iconLeft} aria-hidden="true">
            {leftIcon}
          </span>
        )}

        <input
          ref={ref}
          id={id}
          className={inputClass}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          {...rest}
        />

        {rightIcon && (
          <span className={styles.iconRight} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </div>
    </FieldWrapper>
  );
});

// ── Select ── //
const Select = forwardRef(function Select(
  {
    label,
    required,
    labelHint,
    labelIcon,
    tooltip,
    error,
    hint,
    id,
    htmlFor,
    size = "md",
    shake = false,
    chevron,
    children,
    className = "",
    labelVariant,
    ...rest
  },
  ref,
) {
  const selectClass = [
    styles.select,
    styles[size],
    error ? styles.selectError : "",
    shake ? styles.shake : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <FieldWrapper
      label={label}
      required={required}
      labelHint={labelHint}
      labelIcon={labelIcon}
      tooltip={tooltip}
      error={error}
      hint={hint}
      id={id}
      htmlFor={htmlFor}
      className={className}
      labelVariant={labelVariant}
    >
      <div className={styles.selectWrap}>
        <select
          ref={ref}
          id={id}
          className={selectClass}
          aria-invalid={!!error}
          {...rest}
        >
          {children}
        </select>

        <span className={styles.selectChevron} aria-hidden="true">
          {chevron ?? <IconChevronDown size={16} />}
        </span>
      </div>
    </FieldWrapper>
  );
});

// ── Textarea ── //
const Textarea = forwardRef(function Textarea(
  {
    label,
    required,
    labelHint,
    labelIcon,
    tooltip,
    error,
    hint,
    id,
    htmlFor,
    size = "md",
    maxLength,
    maxWords, // word-count mode — display only, doesn't block typing
    shake = false,
    className = "",
    value = "",
    onChange,
    labelVariant,
    ...rest
  },
  ref,
) {
  const textareaClass = [
    styles.textarea,
    styles[size],
    error ? styles.textareaError : "",
    shake ? styles.shake : "",
  ]
    .filter(Boolean)
    .join(" ");

  // ── Character-count mode (default) ──
  const charsUsed = String(value).length;
  const charsRemaining = maxLength ? maxLength - charsUsed : null;

  // ── Word-count mode ──
  const wordCount = (() => {
    const trimmed = String(value).trim();
    return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
  })();

  const showCounter = !!maxLength || !!maxWords;
  const isWordMode = !!maxWords;

  const used = isWordMode ? wordCount : charsUsed;
  const max = isWordMode ? maxWords : maxLength;
  const isOver = isWordMode ? used > max : used >= max;
  const isWarning = isWordMode
    ? used > max * 0.8 && used <= max
    : used >= max - 50 && used < max;

  const counterClass = showCounter
    ? [
        styles.counter,
        isOver ? styles.counterDanger : "",
        isWarning ? styles.counterWarning : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <FieldWrapper
      label={label}
      required={required}
      labelHint={labelHint}
      labelIcon={labelIcon}
      tooltip={tooltip}
      error={error}
      hint={hint}
      id={id}
      htmlFor={htmlFor}
      className={className}
      labelVariant={labelVariant}
    >
      <textarea
        ref={ref}
        id={id}
        className={textareaClass}
        maxLength={isWordMode ? undefined : maxLength}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        {...rest}
      />

      {showCounter && (
        <div className={counterClass}>
          {isWordMode ? (
            <>
              <span>Max {maxWords} words</span>
              <span style={{ marginLeft: "auto" }}>
                {used} / {max}
              </span>
            </>
          ) : (
            <>
              <span>
                {used >= max
                  ? "Character limit reached"
                  : charsRemaining <= 50
                    ? `${charsRemaining} character${charsRemaining === 1 ? "" : "s"} remaining`
                    : ""}
              </span>
              <span style={{ marginLeft: "auto" }}>
                {used} / {max}
              </span>
            </>
          )}
        </div>
      )}
    </FieldWrapper>
  );
});

export default Input;
export { Select, Textarea };

export const FormInput = ({
  placeholder,
  onChange,
  isValid,
  hasSubmitted,
  name,
  label,
  errorMessage,
  extraClasses,
}) => {
  return (
    <label className={`${extraClasses} ${classes.label}`}>
      {label}
      <input
        type="text"
        placeholder={placeholder}
        onChange={onChange}
        name={name}
        className={!isValid && hasSubmitted ? classes.err : ""}
      />

      {!isValid && hasSubmitted && (
        <span className={classes.error}>{errorMessage}</span>
      )}
    </label>
  );
};
